import React from 'react';
import { ArrowRight } from 'lucide-react';

/**
 * Red banner shown when the customer has a recent failed/abandoned Square
 * card payment on this table. Extracted from CustomerPage.js.
 *
 * Props:
 *   attempt: { order_status, payment_status, total, item_count, checkout_url, voided_reason } | null
 *   onDismiss: () => void
 */
export default function FailedPaymentBanner({ attempt, onDismiss }) {
  if (!attempt) return null;
  const isVoided = attempt.order_status === 'voided';
  const itemCount = attempt.item_count || 0;
  const itemPlural = itemCount === 1 ? '' : 's';
  const total = (attempt.total || 0).toFixed(2);

  return (
    <div
      className="mx-4 mt-3 rounded-sm border border-red-500/30 bg-red-500/10 p-3 flex items-start gap-3"
      data-testid="failed-payment-banner"
    >
      <div className="w-8 h-8 rounded-sm bg-red-500/15 border border-red-500/30 flex items-center justify-center shrink-0 mt-0.5">
        <span className="text-red-400 text-sm font-bold">!</span>
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-sans text-red-300 font-medium">
          {isVoided ? "Your last payment didn’t go through" : 'A previous payment is still pending'}
        </p>
        <p className="text-[12px] text-zinc-400 font-sans mt-0.5 leading-snug">
          {isVoided
            ? `That order (${itemCount} item${itemPlural} · $${total}) was cancelled — feel free to add items and try again.`
            : `${itemCount} item${itemPlural} · $${total} — finish checkout or it’ll auto-cancel in ~15 min.`}
        </p>
        {attempt.checkout_url && !isVoided && (
          <a
            href={attempt.checkout_url}
            className="inline-flex items-center gap-1.5 mt-2 px-3 py-1.5 rounded-sm bg-red-500/20 hover:bg-red-500/30 border border-red-500/40 text-red-200 text-xs font-sans font-semibold transition-colors"
            data-testid="failed-payment-retry"
          >
            Retry payment <ArrowRight className="w-3 h-3" />
          </a>
        )}
      </div>
      <button
        onClick={onDismiss}
        className="text-zinc-500 hover:text-zinc-300 text-xs font-sans shrink-0"
        data-testid="failed-payment-dismiss"
        aria-label="Dismiss"
      >
        ✕
      </button>
    </div>
  );
}
